import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Container, Card, Row, Col, Form, Modal } from "react-bootstrap";
import { MdEdit, MdDelete } from "react-icons/md";
import { FaEye } from "react-icons/fa";
import { Tooltip } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import Select from "react-select"; 
import loading from "../../assets/images/common/loading.gif";
import AppDataGrid from "../../components/common/AppDataGrid";

const SalaryAdvance = () => {
  const [advances, setAdvances] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [components, setComponents] = useState([]);
  const [form, setForm] = useState({
    advance_id: "",
    employee_id: "",
    component_id: "",
    amount: "",
    monthly_deductible: "",
    start_date: "",
    end_date: "",
    balance: "",
  });
  const [editId, setEditId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [advWithDeductionsMap, setAdvWithDeductionsMap] = useState({});
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleAdvance, setScheduleAdvance] = useState(null);

  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const userObj = JSON.parse(sessionStorage.getItem("user")) || {};
  const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge", "Principal"];
  const canSubmit = !readOnlyRoles.includes(userObj.role_name?.trim());

  const isPositiveNumber = (val) => {
    const n = Number(val);
    return Number.isFinite(n) && n > 0;
  };

  // Format dates as DD-MM-YYYY for display (inputs remain native)
  const formatDate = (d) => {
    if (!d) return "-";
    const dateObj = typeof d === "string" ? new Date(d) : d;
    if (isNaN(dateObj)) return String(d);
    const dd = String(dateObj.getDate()).padStart(2, "0");
    const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
    const yyyy = dateObj.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  const normalizeAdvances = (arr = []) =>
    arr.map((x) => ({
      advance_id: x.advance_id,
      employee_id: x.emp_id,
      employee_name: x.emp_name ?? "",
      component_id: x.salary_component_id ?? null,
      component_name: x.salary_component_name ?? "",
      amount: x.advance_amount ?? null,
      monthly_deductible: x.monthly_deductible ?? null,
      start_date: x.start_date ?? null,
      end_date: x.end_date ?? null,
      balance: x.balance_to_be_paid ?? null,
    }));

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const schoolId = userObj.school_id;

      let raw = [];
      try {
        const r1 = await axios.post(`${baseUrl}/salaryadvance/advances-by-school`, { school_id: schoolId });
        raw = r1?.data || [];
      } catch (e1) {
        try {
          const r2 = await axios.post(`${baseUrl}/main/salaryadvance/advances-by-school`, { school_id: schoolId });
          raw = r2?.data || [];
        } catch (e2) {
          try {
            const r3 = await axios.get(`${baseUrl}/salaryadvance/by-school/${schoolId}`);
            raw = r3?.data || [];
          } catch (e3) {
            const r4 = await axios.get(`${baseUrl}/main/salaryadvance/by-school/${schoolId}`);
            raw = r4?.data || [];
          }
        }
      }

      const map = {};
      raw.forEach((item) => {
        map[item.advance_id] = item;
      });
      setAdvWithDeductionsMap(map);

      const norm = normalizeAdvances(raw);
      setAdvances(norm);
      setFilteredRecords(norm);
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        "Failed to load salary advances.";
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.post(baseUrl + "/employees/", {
        action: "READ",
        school_id: userObj.school_id,
      });
      setEmployees(response.data);
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        "Failed to load employees.";
      toast.error(msg);
    }
  };

  const fetchComponents = async () => {
    try {
      const response = await axios.post(baseUrl + "/salarycomponent/", {
        action: "READ",
        school_id: userObj.school_id,
      });
      setComponents(response.data);
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        "Failed to load components.";
      toast.error(msg);
    }
  };

  useEffect(() => {
    fetchData();
    fetchEmployees();
    fetchComponents();
  }, []);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setForm((prev) => {
      const updated = { ...prev, [id]: value };
      const amount = parseFloat(updated.amount) || 0;
      const monthly = parseFloat(updated.monthly_deductible) || 0;
      const startDate = updated.start_date ? new Date(updated.start_date) : null;

      if (amount && monthly && startDate) {
        const months = Math.ceil(amount / monthly);
        const endDate = new Date(startDate);
        endDate.setMonth(startDate.getMonth() + months - 1);
        updated.end_date = endDate.toISOString().split("T")[0];
        updated.balance = amount.toFixed(2);
      } else if (amount && !monthly) {
        updated.balance = amount.toFixed(2);
      } else if (!amount) {
        updated.balance = "";
      }

      return updated;
    });
  };

  const requestWithFallback = async ({ urls, bodies, methods }) => {
    let lastErr;
    for (const url of urls) {
      for (const method of methods) {
        for (const body of bodies) {
          try {
            if (method === "post") {
              return await axios.post(url, body, { headers: { "Content-Type": "application/json" } });
            }
            if (method === "put") {
              return await axios.put(url, body, { headers: { "Content-Type": "application/json" } });
            }
            if (method === "delete") {
              return await axios.delete(url, { data: body, headers: { "Content-Type": "application/json" } });
            }
          } catch (e) {
            lastErr = e;
          }
        }
      }
    }
    throw lastErr;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.employee_id) return toast.error("Please select an employee.");
    if (!form.component_id) return toast.error("Please select a component.");
    if (!isPositiveNumber(form.amount)) return toast.error("Amount must be > 0.");
    if (!isPositiveNumber(form.monthly_deductible)) return toast.error("Monthly Deductible must be > 0.");
    if (Number(form.monthly_deductible) > Number(form.amount)) {
      return toast.error("Monthly Deductible cannot be greater than Advance Amount.");
    }
    if (!form.start_date) return toast.error("Please choose a start date.");

    try {
      if (editId) {
        const id = Number(form.advance_id);

        const common = {
          school_id: userObj.school_id,
          created_by: userObj?.email || userObj?.user_name || "system",
          employee_id: Number(form.employee_id),
          component_id: Number(form.component_id),
          amount: Number(form.amount),
          monthly_deductible: Number(form.monthly_deductible),
          start_date: form.start_date,
          end_date: form.end_date,
          balance: form.balance === "" ? null : Number(form.balance),
          deduction_component_name: "Advance Deduction",
        };

        const urls = [
          `${baseUrl}/salaryadvance`,
          `${baseUrl}/salaryadvance/`,
          `${baseUrl}/main/salaryadvance`,
          `${baseUrl}/main/salaryadvance/`,
          `${baseUrl}/salaryadvance/update`,
          `${baseUrl}/main/salaryadvance/update`,
          `${baseUrl}/salaryadvance/manage`,
          `${baseUrl}/main/salaryadvance/manage`,
          `${baseUrl}/salaryadvance/${id}`,
          `${baseUrl}/main/salaryadvance/${id}`,
        ];

        const bodies = [
          { action: "UPDATE", advance_id: id, ...common },
          { action: "UPDATE_ADVANCE", advance_id: id, ...common },
          { action: "UPDATE", employee_bonus_id: id, ...common },
          { action: "EDIT", id, ...common },
          { id, ...common },
        ];

        const methods = ["post", "put"];

        await requestWithFallback({ urls, bodies, methods });
        toast.success("Salary advance updated");
      } else {
        const payload = {
          emp_id: Number(form.employee_id),
          school_id: userObj.school_id,
          advance_component_id: Number(form.component_id),
          amount: Number(form.amount),
          monthly_deductible: Number(form.monthly_deductible),
          start_date: form.start_date,
          end_date: form.end_date || null,
          created_by: userObj?.email || userObj?.user_name || "system",
          reason_extra: null,
          deduction_component_name: "Advance Deduction",
        };
        try {
          await axios.post(`${baseUrl}/salaryadvance/create`, payload, { headers: { "Content-Type": "application/json" } });
        } catch {
          await axios.post(`${baseUrl}/main/salaryadvance/create`, payload, { headers: { "Content-Type": "application/json" } });
        }
        toast.success("Salary advance added");
      }

      handleResetForm();
      fetchData();
    } catch (err) {
      const status = err?.response?.status;
      const url = err?.config?.url;
      const serverMsg = err?.response?.data?.error || err?.response?.data?.detail;
      toast.error(serverMsg || `Failed to submit salary advance. ${status ? `Status ${status}` : ""} ${url ? `(${url})` : ""}`);
    }
  };

  const handleResetForm = () => {
    setForm({
      advance_id: "",
      employee_id: "",
      component_id: "",
      amount: "",
      monthly_deductible: "",
      start_date: "",
      end_date: "",
      balance: "",
    });
    setEditId(null);
    setFilteredRecords(advances);
  };

  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase().trim();
    setSearchQuery(query);
    const filtered = advances.filter((item) =>
      ["employee_name", "component_name", "amount", "start_date"].some((key) =>
        String(item[key] || "").toLowerCase().includes(query)
      )
    );
    setFilteredRecords(filtered);
  };

  const handleViewSchedule = (advance_id) => {
    const sched = advWithDeductionsMap[advance_id];
    if (!sched) {
      toast.info("No schedule found for this advance yet.");
      return;
    }
    setScheduleAdvance(sched);
    setShowScheduleModal(true);
  };

  // 🔹 Restrict component dropdown to specific items
  const componentWhitelist = new Set(["Salary Advance", "Advance Deduction"]);
  const filteredComponents = (components || []).filter((c) => componentWhitelist.has(c.component_name));

  // 🔹 Build react-select options
  const employeeOptions = (employees || []).map((e) => ({
    value: e.emp_id,
    label: e.emp_name,
  }));
  const componentOptions = (filteredComponents || []).map((c) => ({
    value: c.salary_component_id,
    label: c.component_name,
  }));

  const columns = [
    { field: "employee_name", headerName: "Employee", flex: 1 },
    { field: "amount", headerName: "Amount", flex: 1 },
    { field: "monthly_deductible", headerName: "Monthly Deductible", flex: 1 },
    {
      field: "start_date",
      headerName: "Start Date",
      flex: 1,
      renderCell: (params) => <span>{formatDate(params.value)}</span>,
    },
    {
      field: "end_date",
      headerName: "End Date",
      flex: 1,
      renderCell: (params) => <span>{formatDate(params.value)}</span>,
    },
    { field: "balance", headerName: "Balance To Be Paid", flex: 1 },
    ...(canSubmit
      ? [
        {
          field: "actions",
          headerName: "Actions",
          flex: 1,
          sortable: false,
          renderCell: (params) => (
            <div className="tableActions">
              <Tooltip title="View Schedule" arrow>
                <span
                  className="commonActionIcons"
                  onClick={() => handleViewSchedule(params.row.advance_id)}
                  style={{ marginRight: 10 }}
                >
                  <FaEye />
                </span>
              </Tooltip>

              {/* <Tooltip title="Edit" arrow>
                  <span
                    className="commonActionIcons"
                    onClick={() => handleEditClick(params.row.advance_id)}
                  >
                    <MdEdit />
                  </span>
                </Tooltip>
                <Tooltip title="Delete" arrow>
                  <span
                    className="commonActionIcons"
                    onClick={() => handleDeleteClick(params.row.advance_id)}
                  >
                    <MdDelete />
                  </span>
                </Tooltip> */}
            </div>
          ),
        },
      ]
      : []),
  ];

  const formatReasonShort = (reason) => {
    if (!reason) return "-";
    const m = reason.match(/deduction\s+for\s+[A-Za-z]{3}\s+\d{4}/i);
    return m ? m[0] : reason;
  };

  return (
    <div className="pageBody">
      <style>{`
  /* 🔹 Additional rule to widen modal — non-destructive */
  .fit-modal .modal-dialog {
    width: 96vw !important;   /* occupy most of the viewport width */
    max-width: 1700px !important;  /* allow full width on large screens */
  }

  @media (max-width: 992px) {
    .fit-modal .modal-dialog {
      width: 98vw !important;   /* keep mobile responsive */
      max-width: 98vw !important;
    }
  }
`}</style>

      <ToastContainer />
      <h6 className="commonTableTitle"> Salary Advance</h6>

      <section id="salaryAdvanceFormSection">
        <Container fluid>
          <Card>
            <Card.Body>
              <form onSubmit={handleSubmit}>
                <Row>
                  <Col xs={12}>
                    <h6 className="commonSectionTitle">
                      {editId ? "Edit Salary Advance" : "Add Salary Advance"}
                    </h6>
                  </Col>
                </Row>

                <Row>
                  {/* 🔹 Employee (searchable react-select) */}
                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>
                          Employee <span className="requiredStar">*</span>
                        </Form.Label>
                        <Select
                          inputId="employee_id"
                          classNamePrefix="react-select"
                          placeholder="--Select Employee--"
                          isSearchable
                          isClearable
                          value={
                            form.employee_id
                              ? employeeOptions.find((o) => o.value === Number(form.employee_id))
                              : null
                          }
                          onChange={(opt) =>
                            setForm((f) => ({
                              ...f,
                              employee_id: opt ? String(opt.value) : "",
                            }))
                          }
                          options={employeeOptions}
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  {/* 🔹 Component (restricted + searchable react-select) */}
                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>
                          Component <span className="requiredStar">*</span>
                        </Form.Label>
                        <Select
                          inputId="component_id"
                          classNamePrefix="react-select"
                          placeholder="--Select Component--"
                          isSearchable
                          isClearable
                          value={
                            form.component_id
                              ? componentOptions.find((o) => o.value === Number(form.component_id))
                              : null
                          }
                          onChange={(opt) =>
                            setForm((f) => ({
                              ...f,
                              component_id: opt ? String(opt.value) : "",
                            }))
                          }
                          options={componentOptions}
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  {/* Amount */}
                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>
                          Enter Amount <span className="requiredStar">*</span>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          id="amount"
                          value={form.amount}
                          onChange={handleInputChange}
                          required
                          min="0"
                          step="0.01"
                          placeholder="e.g., 25000.00"
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  {/* Monthly Deductible */}
                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>
                          Monthly Deductible <span className="requiredStar">*</span>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          id="monthly_deductible"
                          value={form.monthly_deductible}
                          onChange={handleInputChange}
                          required
                          min="0"
                          step="0.01"
                          placeholder="e.g., 5000.00"
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  {/* Start Date */}
                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>
                          Start Date <span className="requiredStar">*</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          id="start_date"
                          value={form.start_date}
                          onChange={handleInputChange}
                          required
                          placeholder="DD-MM-YYYY"
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  {/* End Date (auto) */}
                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>End Date</Form.Label>
                        <Form.Control
                          type="date"
                          id="end_date"
                          value={form.end_date}
                          onChange={handleInputChange}
                          readOnly
                          plaintext={false}
                          placeholder="Auto-calculated"
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  {/* Balance (auto) */}
                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>Balance To Be Paid</Form.Label>
                        <Form.Control
                          type="text"
                          id="balance"
                          value={form.balance}
                          onChange={handleInputChange}
                          readOnly
                          plaintext={false}
                          placeholder="Auto-calculated"
                        />
                      </Form.Group>
                    </div>
                  </Col>
                </Row>

                <div className="d-flex justify-content-between mt-3">
                  <Button
                    type="button"
                    variant="primary"
                    className="btn-info clearBtn"
                    onClick={handleResetForm}
                  >
                    Reset
                  </Button>
                  {canSubmit && (
                    <Button type="submit" variant="primary" className="btn-success primaryBtn">
                      {editId ? "Update" : "Add"}
                    </Button>
                  )}
                </div>
              </form>
            </Card.Body>
          </Card>
        </Container>
      </section>

      {/* Grid + Search */}
      <section className="mt-4">
        <div className="commonDataTableHead d-flex justify-content-end">
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            className="searchInput"
            onChange={handleSearchChange}
          />
        </div>

        <div
          className="commonTable height100"
          style={{ maxHeight: "calc(100vh - 350px)", overflowY: "auto" }}
        >
          <div className="tableBody">
            {isLoading ? (
              <div className="loadingContainer">
                <img src={loading} alt="Loading..." className="loadingGif" />
              </div>
            ) : (
              <AppDataGrid
                rows={filteredRecords}
                columns={columns}
                getRowId={(row) => row.advance_id}
                showToolbar={false}
                pagination
                pageSizeOptions={[10, 20, 50]}
                density="compact"
                maxHeight={550}
              />
            )}
          </div>
        </div>
      </section>

      {/* Schedule Modal (no horizontal scroll, all four columns visible) */}
      <Modal
        show={showScheduleModal}
        onHide={() => setShowScheduleModal(false)}
        size="xl"
        centered
        dialogClassName="fit-modal"
      >

        <Modal.Header closeButton>
          <Modal.Title>Deduction Schedule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!scheduleAdvance ? (
            <div>No schedule found.</div>
          ) : (
            <>
              <div className="mb-2">
                <strong>Employee:</strong> {scheduleAdvance.emp_name || "-"}
              </div>
              <div className="mb-2">
                <strong>Advance Amount:</strong> {scheduleAdvance.advance_amount ?? "-"}
              </div>
              <div className="mb-2">
                <strong>Monthly Deductible:</strong> {scheduleAdvance.monthly_deductible ?? "-"}
              </div>
              <div className="mb-3">
                <strong>Start:</strong> {formatDate(scheduleAdvance.start_date)}{" "}
                <strong className="ms-3">End:</strong> {formatDate(scheduleAdvance.end_date)}
              </div>

              <div className="table-responsive">
                <table className="table table-sm table-striped">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Reason</th>
                      <th>Monthly</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(scheduleAdvance.deductions || []).map((d, idx) => (
                      <tr key={d.employee_bonus_id || idx}>
                        <td>{idx + 1}</td>
                        <td>{formatReasonShort(d.reason)}</td>
                        <td>{d.monthly_deductible ?? "-"}</td>
                        <td>{d.balance_to_be_paid ?? "-"}</td>
                      </tr>
                    ))}
                    {(!scheduleAdvance.deductions || scheduleAdvance.deductions.length === 0) && (
                      <tr>
                        <td colSpan={4} className="text-center">
                          No deductions generated.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowScheduleModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SalaryAdvance;
